import { useCallback, useMemo, useState } from 'react';

import {
  FilterChip,
  FilterEditorComponentProps,
  FilterViewerComponentProps,
  GroupedAutocomplete,
  SearchUtils,
  createFilter,
} from '@spotify/confidence-core-react';

import { LabelDefinition } from '../../hooks/useLabelsForResource';

export const LABEL_FILTER_ID = 'labels';

export const formatLabelValue = (labelId: string, value: string) =>
  `${labelId}.${value}`;

const parseLabelValue = (
  compositeValue: string,
): { group: string; item: string } | null => {
  const idx = compositeValue.lastIndexOf('.');
  if (idx <= 0 || idx === compositeValue.length - 1) return null;
  return {
    group: compositeValue.slice(0, idx),
    item: compositeValue.slice(idx + 1),
  };
};

const toRecord = (flatValues: string[]): Record<string, string[]> => {
  const record: Record<string, string[]> = {};
  for (const v of flatValues) {
    const parsed = parseLabelValue(v);
    if (parsed) {
      (record[parsed.group] ??= []).push(parsed.item);
    }
  }
  return record;
};

const toFlat = (record: Record<string, string[]>): string[] =>
  Object.entries(record).flatMap(([group, items]) =>
    items.map(item => formatLabelValue(group, item)),
  );

const GroupedLabelFilterEditor = ({
  value,
  onValueChange,
  labelDefinitions,
}: FilterEditorComponentProps<string[]> & {
  labelDefinitions: LabelDefinition[];
}) => {
  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  const groupItems = useMemo(
    () => labelDefinitions.map(d => ({ value: d.id, label: d.displayName })),
    [labelDefinitions],
  );

  const activeDef = labelDefinitions.find(d => d.id === activeLabel);
  const valueItems = useMemo(
    () => (activeDef?.allowedValues ?? []).map(v => ({ value: v })),
    [activeDef],
  );

  const selectedItems = useMemo(() => toRecord(value ?? []), [value]);
  const handleChange = useCallback(
    (record: Record<string, string[]>) => onValueChange(toFlat(record)),
    [onValueChange],
  );

  return (
    <GroupedAutocomplete.Root
      activeGroup={activeLabel}
      onActiveGroupChange={setActiveLabel}
      selectedItems={selectedItems}
      onSelectedItemsChange={handleChange}
    >
      <GroupedAutocomplete.Groups
        items={groupItems}
        searchPlaceholder="Search labels..."
        emptyMessage="No labels available"
      />
      <GroupedAutocomplete.Items
        items={valueItems}
        searchPlaceholder="Search values..."
        emptyMessage="No values for this label"
      />
    </GroupedAutocomplete.Root>
  );
};

const GroupedLabelFilterViewer = (
  props: FilterViewerComponentProps<string[]> & {
    labelDefinitions: LabelDefinition[];
  },
) => {
  const { value, labelDefinitions } = props;

  const getLabel = () => {
    if (value.length === 0) return 'No labels';
    if (value.length === 1) {
      const parsed = parseLabelValue(value[0]);
      if (!parsed) return value[0];
      const def = labelDefinitions.find(d => d.id === parsed.group);
      return `${def?.displayName ?? parsed.group}: ${parsed.item}`;
    }
    return `Labels (${value.length})`;
  };

  return <FilterChip {...props} label={getLabel()} />;
};

export const createGroupedLabelFilter = (labelDefinitions: LabelDefinition[]) =>
  createFilter<string[]>({
    id: LABEL_FILTER_ID,
    label: 'Labels',
    query: (values: string[]) => {
      if (values.length === 0) return '';
      return values
        .map(v => {
          const parsed = parseLabelValue(v);
          if (!parsed) return '';
          return `labels.${SearchUtils.escapeQuery(parsed.group)}:"${SearchUtils.escapeQuery(parsed.item)}"`;
        })
        .filter(Boolean)
        .join(' OR ');
    },
    viewer: props => (
      <GroupedLabelFilterViewer
        labelDefinitions={labelDefinitions}
        {...props}
      />
    ),
    editor: props => (
      <GroupedLabelFilterEditor
        labelDefinitions={labelDefinitions}
        {...props}
      />
    ),
  });
